import React, { Suspense } from 'react';
import './setupProdLogging';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import './App.css';
// Defer Toaster to post-load to keep initial paint lean
import DeferredToaster from './components/DeferredToaster';
import { AuthProvider } from './contexts/AuthContext';
import { DataProvider } from './contexts/DataContext';
import { Header } from './components/Header';
import Footer from './components/Footer';
import DeferredConsentBanner from './components/DeferredConsentBanner';
import { ProtectedRoute } from './components/ProtectedRoute';
import AnalyticsWrapper from './components/AnalyticsWrapper';
import { PreferencesSkeleton, MealPlannerSkeleton } from './components/RouteSkeletons';
import RoutePrefetcher from './components/RoutePrefetcher';
import HomePage from './pages/HomePage';
const NotFoundPage = React.lazy(() => import('./pages/NotFoundPage'));
const ForbiddenPage = React.lazy(() => import('./pages/ForbiddenPage'));
const ServerErrorPage = React.lazy(() => import('./pages/ServerErrorPage'));
const WelcomePage = React.lazy(() => import('./pages/WelcomePage'));
const MealPlannerPage = React.lazy(() => import('./pages/MealPlannerPage'));
const PreferencesPage = React.lazy(() => import('./pages/PreferencesPage'));
const EmailVerificationPage = React.lazy(() => import('./pages/EmailVerificationPage'));
const ResendVerificationPage = React.lazy(() => import('./pages/ResendVerificationPage'));
const AuthPage = React.lazy(() => import('./pages/AuthPage'));
const ContactSupportPage = React.lazy(() => import('./pages/ContactSupportPage'));
const ForgotPasswordPage = React.lazy(() => import('./pages/ForgotPasswordPage'));
const ResetPasswordPage = React.lazy(() => import('./pages/ResetPasswordPage'));
const AccountSettingsPage = React.lazy(() => import('./pages/AccountSettingsPage'));
const AdminPage = React.lazy(() => import('./pages/AdminPage'));
const SubscriptionPage = React.lazy(() => import('./pages/SubscriptionPage'));
const SubscriptionSuccessPage = React.lazy(() => import('./pages/SubscriptionSuccessPage'));
const TermsPage = React.lazy(() => import('./pages/TermsPage'));
const PrivacyPolicyPage = React.lazy(() => import('./pages/PrivacyPolicyPage'));
const MicrosoftAuthCallbackPage = React.lazy(() => import('./pages/MicrosoftAuthCallback'));

function App() {
  return (
    <AuthProvider>
        <Router
          future={{
            v7_startTransition: true,
            v7_relativeSplatPath: true
          }}
        >
          <AnalyticsWrapper>
            <RoutePrefetcher />
            <div className="min-h-screen bg-gradient-to-br from-green-50 to-blue-50 flex flex-col">
            <Header />
      <main id="main-content" className="container mx-auto px-3 sm:px-4 page pb-0 flex-1 w-full" style={{ minHeight: '60vh' }}>
              <Suspense fallback={<div className="p-8" style={{ minHeight: '60vh' }} aria-busy="true" aria-live="polite" /> }>
              <Routes>
                <Route path="/" element={<HomePage />} />
                <Route path="/welcome" element={<WelcomePage />} />
                <Route path="/meal-planner" element={
                  <Suspense fallback={<MealPlannerSkeleton />}>
                    <DataProvider>
                      <ProtectedRoute requirePreferences={true}>
                        <MealPlannerPage />
                      </ProtectedRoute>
                    </DataProvider>
                  </Suspense>
                } />
                <Route path="/preferences" element={
                  <Suspense fallback={<PreferencesSkeleton />}>
                    <DataProvider>
                      <ProtectedRoute requireAuth={false}>
                        <PreferencesPage />
                      </ProtectedRoute>
                    </DataProvider>
                  </Suspense>
                } />
                <Route path="/account-settings" element={
                  <DataProvider>
                    <ProtectedRoute requireAuth={true}>
                      <AccountSettingsPage />
                    </ProtectedRoute>
                  </DataProvider>
                } />
                <Route path="/admin" element={
                  <DataProvider>
                    <ProtectedRoute requireAuth={true}>
                      <AdminPage />
                    </ProtectedRoute>
                  </DataProvider>
                } />
                <Route path="/subscription" element={<SubscriptionPage />} />
                <Route path="/subscription/cancel" element={<SubscriptionPage />} />
                <Route path="/subscription/cancelled" element={<SubscriptionPage />} />
                <Route path="/subscription/success" element={<SubscriptionSuccessPage />} />
                <Route path="/auth" element={
                  <DataProvider>
                    <AuthPage />
                  </DataProvider>
                } />
                <Route path="/auth/microsoft/callback" element={<MicrosoftAuthCallbackPage />} />
                <Route path="/verify-email" element={<EmailVerificationPage />} />
                <Route path="/resend-verification" element={<ResendVerificationPage />} />
                <Route path="/forgot-password" element={<ForgotPasswordPage />} />
                <Route path="/reset-password" element={<ResetPasswordPage />} />
                <Route path="/support" element={<ContactSupportPage />} />
                <Route path="/terms" element={<TermsPage />} />
                <Route path="/privacy" element={<PrivacyPolicyPage />} />
                <Route path="/403" element={<ForbiddenPage />} />
                <Route path="/500" element={<ServerErrorPage />} />
                <Route path="*" element={<NotFoundPage />} />
                { /* Privacy controls now handled via footer toggle */ }
              </Routes>
              </Suspense>
            </main>
            <DeferredConsentBanner />
            <Footer />
            <DeferredToaster />
          </div>
          </AnalyticsWrapper>
        </Router>
    </AuthProvider>
  );
}

export default App;

